import React, { useState, Component } from 'react';
import Layout from '../../components/Layout';
import { BarChart3Icon, GraduationCapIcon, UsersIcon, CalendarIcon, ArrowRightIcon, SearchIcon, FilterIcon, ChevronDownIcon, AlertCircleIcon, TrendingUpIcon, UsersRoundIcon, ExternalLinkIcon, FileTextIcon, ClockIcon, MoreHorizontalIcon, CheckIcon, XIcon, AlertTriangleIcon, EyeIcon, DownloadIcon, MessageSquareIcon, CheckCircleIcon, XCircleIcon, FlagIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
// Confirmation Modal Component
const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmButtonText,
  confirmButtonColor,
  icon
}) => {
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby={title} role="dialog" aria-modal="true" data-id="element-6476">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-6477">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose} data-id="element-6478"></div>
        {/* Modal positioning */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-6479">
          &#8203;
        </span>
        {/* Modal content */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-6480">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-6481">
            <div className="sm:flex sm:items-start" data-id="element-6482">
              <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${confirmButtonColor === 'bg-red-600' ? 'bg-red-100' : 'bg-green-100'} sm:mx-0 sm:h-10 sm:w-10`} data-id="element-6483">
                {icon}
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left" data-id="element-6484">
                <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title" data-id="element-6485">
                  {title}
                </h3>
                <div className="mt-2" data-id="element-6486">
                  <p className="text-sm text-gray-500" data-id="element-6487">{message}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-6488">
            <button type="button" className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 ${confirmButtonColor} text-base font-medium text-white hover:${confirmButtonColor.replace('600', '700')} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:${confirmButtonColor.replace('bg-', 'ring-')} sm:ml-3 sm:w-auto sm:text-sm`} onClick={onConfirm} data-id="element-6489">
              {confirmButtonText}
            </button>
            <button type="button" className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" onClick={onClose} data-id="element-6490">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>;
};
// Helper function to get status color
const getStatusColor = status => {
  switch (status) {
    case 'accepted':
      return 'bg-green-100 text-green-800';
    case 'rejected':
      return 'bg-red-100 text-red-800';
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'review':
      return 'bg-blue-100 text-blue-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};
// Helper function to get eligibility icon
const getEligibilityIcon = eligibility => {
  switch (eligibility) {
    case 'Eligible':
      return <CheckIcon className="w-5 h-5 text-green-500" data-id="element-6491" />;
    case 'Ineligible':
      return <XIcon className="w-5 h-5 text-red-500" data-id="element-6492" />;
    case 'Needs Review':
      return <AlertTriangleIcon className="w-5 h-5 text-yellow-500" data-id="element-6493" />;
    default:
      return <AlertTriangleIcon className="w-5 h-5 text-gray-500" data-id="element-6494" />;
  }
};
const UniversityDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [applicationFilter, setApplicationFilter] = useState('all');
  // Application detail drawer states
  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  // Confirmation modal states
  const [isAcceptModalOpen, setIsAcceptModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [actionApplicationId, setActionApplicationId] = useState(null);
  // Filter applications based on status
  const filterApplications = (status: string) => {
    setApplicationFilter(status);
  };
  // Application data
  const applications = [{
    id: 1,
    name: 'Sarah Ahmed',
    program: 'MBA Program',
    status: 'pending',
    avatar: 'SA',
    date: '15 Jun 2023',
    eligibility: 'Eligible',
    studentId: 'STU12345',
    submissionDate: '2023-06-15',
    lastUpdated: '2023-06-16',
    aiSummary: 'Strong candidate with high GPA and relevant work experience',
    flags: [],
    tags: ['High GPA', 'Work Experience'],
    documents: ['Transcript', 'CV', 'Recommendation Letter']
  }, {
    id: 2,
    name: 'Ali Mohammed',
    program: 'Computer Science',
    status: 'accepted',
    avatar: 'AM',
    date: '12 Jun 2023',
    eligibility: 'Eligible',
    studentId: 'STU12346',
    submissionDate: '2023-06-12',
    lastUpdated: '2023-06-14',
    aiSummary: 'Promising candidate with strong technical background',
    flags: [],
    tags: ['Technical Skills', 'High GPA'],
    documents: ['Transcript', 'CV', 'Portfolio']
  }, {
    id: 3,
    name: 'Maryam Khan',
    program: 'Business Analytics',
    status: 'review',
    avatar: 'MK',
    date: '10 Jun 2023',
    eligibility: 'Needs Review',
    studentId: 'STU12347',
    submissionDate: '2023-06-10',
    lastUpdated: '2023-06-12',
    aiSummary: 'Good academic background, missing one recommendation letter',
    flags: ['Missing recommendation letter'],
    tags: ['Research Experience'],
    documents: ['Transcript', 'CV']
  }, {
    id: 4,
    name: 'Hassan Al Farsi',
    program: 'Data Science',
    status: 'pending',
    avatar: 'HF',
    date: '9 Jun 2023',
    eligibility: 'Eligible',
    studentId: 'STU12348',
    submissionDate: '2023-06-09',
    lastUpdated: '2023-06-10',
    aiSummary: 'Strong technical skills with previous project experience',
    flags: [],
    tags: ['Technical Skills', 'Project Experience'],
    documents: ['Transcript', 'CV', 'Portfolio']
  }, {
    id: 5,
    name: 'Fatima Al Zaabi',
    program: 'International Relations',
    status: 'accepted',
    avatar: 'FZ',
    date: '5 Jun 2023',
    eligibility: 'Eligible',
    studentId: 'STU12349',
    submissionDate: '2023-06-05',
    lastUpdated: '2023-06-07',
    aiSummary: 'Excellent academic record with international experience',
    flags: [],
    tags: ['International Experience', 'High GPA'],
    documents: ['Transcript', 'CV', 'Recommendation Letters (2)']
  }];
  // Filter applications based on selected filter
  const filteredApplications = applicationFilter === 'all' ? applications : applications.filter(app => app.status === applicationFilter);
  // Open application detail drawer
  const openDetailDrawer = application => {
    setSelectedApplication(application);
    setIsDetailDrawerOpen(true);
  };
  // Close application detail drawer
  const closeDetailDrawer = () => {
    setIsDetailDrawerOpen(false);
    setSelectedApplication(null);
  };
  // Format date to readable format
  const formatDate = dateString => {
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  // Handle showing accept confirmation modal
  const handleShowAcceptModal = applicationId => {
    setActionApplicationId(applicationId);
    setIsAcceptModalOpen(true);
  };
  // Handle showing reject confirmation modal
  const handleShowRejectModal = applicationId => {
    setActionApplicationId(applicationId);
    setIsRejectModalOpen(true);
  };
  // Handle accept confirmation
  const handleAcceptConfirm = () => {
    // Handle single application accept
    console.log('Accepting application:', actionApplicationId);
    // In a real app, you would call an API to update this application
    toast.success(`Application has been accepted.`, {
      duration: 4000,
      icon: <CheckCircleIcon className="w-5 h-5" data-id="element-6495" />
    });
    // If the application is in the drawer, close it
    if (isDetailDrawerOpen && selectedApplication && selectedApplication.id === actionApplicationId) {
      setIsDetailDrawerOpen(false);
      setSelectedApplication(null);
    }
    // Close the modal
    setIsAcceptModalOpen(false);
  };
  // Handle reject confirmation
  const handleRejectConfirm = () => {
    // Handle single application reject
    console.log('Rejecting application:', actionApplicationId);
    // In a real app, you would call an API to update this application
    toast.error(`Application has been rejected.`, {
      duration: 4000,
      icon: <XCircleIcon className="w-5 h-5" data-id="element-6496" />
    });
    // If the application is in the drawer, close it
    if (isDetailDrawerOpen && selectedApplication && selectedApplication.id === actionApplicationId) {
      setIsDetailDrawerOpen(false);
      setSelectedApplication(null);
    }
    // Close the modal
    setIsRejectModalOpen(false);
  };
  // Get application name for confirmation messages
  const getApplicationInfo = applicationId => {
    const app = applications.find(a => a.id === applicationId);
    return app ? `${app.name}'s application for ${app.program}` : 'this application';
  };
  return <Layout title="Dashboard Overview" persona="University Coordinator" universityName="UAE Business School" data-id="element-6497">
      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-6498">
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-6499">
          <div className="flex items-start justify-between" data-id="element-6500">
            <div data-id="element-6501">
              <h3 className="font-medium text-gray-800 mb-2" data-id="element-6502">
                Active Programs
              </h3>
              <div className="text-3xl font-bold text-primary-500" data-id="element-6503">12</div>
              <p className="text-sm text-gray-500 mt-1" data-id="element-6504">3 pending approval</p>
            </div>
            <div className="p-3 bg-primary-50 rounded-full" data-id="element-6505">
              <GraduationCapIcon className="w-6 h-6 text-primary-500" data-id="element-6506" />
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100" data-id="element-6507">
            <button onClick={() => navigate('/university/programs')} className="text-sm text-primary-500 font-medium flex items-center hover:underline" data-id="element-6508">
              View all programs
              <ArrowRightIcon className="w-4 h-4 ml-1" data-id="element-6509" />
            </button>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-6510">
          <div className="flex items-start justify-between" data-id="element-6511">
            <div data-id="element-6512">
              <h3 className="font-medium text-gray-800 mb-2" data-id="element-6513">Applications</h3>
              <div className="text-3xl font-bold text-primary-500" data-id="element-6514">48</div>
              <p className="text-sm text-gray-500 mt-1" data-id="element-6515">22 new this week</p>
            </div>
            <div className="p-3 bg-primary-50 rounded-full" data-id="element-6516">
              <UsersIcon className="w-6 h-6 text-primary-500" data-id="element-6517" />
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100" data-id="element-6518">
            <button onClick={() => navigate('/university/applications')} className="text-sm text-primary-500 font-medium flex items-center hover:underline" data-id="element-6519">
              Review applications
              <ArrowRightIcon className="w-4 h-4 ml-1" data-id="element-6520" />
            </button>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-6521">
          <div className="flex items-start justify-between" data-id="element-6522">
            <div data-id="element-6523">
              <h3 className="font-medium text-gray-800 mb-2" data-id="element-6524">
                Acceptance Rate
              </h3>
              <div className="text-3xl font-bold text-primary-500" data-id="element-6525">68%</div>
              <p className="text-sm text-gray-500 mt-1" data-id="element-6526">+5% from last year</p>
            </div>
            <div className="p-3 bg-primary-50 rounded-full" data-id="element-6527">
              <BarChart3Icon className="w-6 h-6 text-primary-500" data-id="element-6528" />
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100" data-id="element-6529">
            <button onClick={() => navigate('/university/reports')} className="text-sm text-primary-500 font-medium flex items-center hover:underline" data-id="element-6530">
              View analytics
              <ArrowRightIcon className="w-4 h-4 ml-1" data-id="element-6531" />
            </button>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-6532">
        {/* Recent Applications */}
        <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-6533">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center" data-id="element-6534">
            <h2 className="text-lg font-medium text-gray-800" data-id="element-6535">
              Recent Applications
            </h2>
            <button onClick={() => navigate('/university/applications')} className="text-sm text-primary-500 hover:underline flex items-center" data-id="element-6536">
              View All
              <ArrowRightIcon className="w-4 h-4 ml-1" data-id="element-6537" />
            </button>
          </div>
          {/* Search and filter bar */}
          <div className="px-6 py-3 border-b border-gray-200 bg-gray-50 flex flex-wrap items-center justify-between gap-3" data-id="element-6538">
            <div className="relative flex-grow max-w-md" data-id="element-6539">
              <input type="text" placeholder="Search applications..." className="w-full pl-10 pr-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" data-id="element-6540" />
              <SearchIcon className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" data-id="element-6541" />
            </div>
            <div className="flex items-center space-x-2" data-id="element-6542">
              <span className="text-sm text-gray-600" data-id="element-6543">Filter:</span>
              <div className="relative" data-id="element-6544">
                <button className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 flex items-center" data-id="element-6545">
                  <FilterIcon className="w-4 h-4 mr-2 text-gray-500" data-id="element-6546" />
                  {applicationFilter === 'all' ? 'All Applications' : applicationFilter === 'pending' ? 'Pending' : applicationFilter === 'review' ? 'Under Review' : 'Accepted'}
                  <ChevronDownIcon className="w-4 h-4 ml-2 text-gray-500" data-id="element-6547" />
                </button>
                <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10 hidden group-focus:block" data-id="element-6548">
                  <div className="py-1" data-id="element-6549">
                    <button onClick={() => filterApplications('all')} className={`w-full text-left px-4 py-2 text-sm ${applicationFilter === 'all' ? 'bg-primary-50 text-primary-500' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-6550">
                      All Applications
                    </button>
                    <button onClick={() => filterApplications('pending')} className={`w-full text-left px-4 py-2 text-sm ${applicationFilter === 'pending' ? 'bg-primary-50 text-primary-500' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-6551">
                      Pending
                    </button>
                    <button onClick={() => filterApplications('review')} className={`w-full text-left px-4 py-2 text-sm ${applicationFilter === 'review' ? 'bg-primary-50 text-primary-500' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-6552">
                      Under Review
                    </button>
                    <button onClick={() => filterApplications('accepted')} className={`w-full text-left px-4 py-2 text-sm ${applicationFilter === 'accepted' ? 'bg-primary-50 text-primary-500' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-6553">
                      Accepted
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Filter tabs */}
          <div className="px-6 py-2 border-b border-gray-200 flex space-x-1" data-id="element-6554">
            <button onClick={() => filterApplications('all')} className={`px-3 py-2 text-sm rounded-md ${applicationFilter === 'all' ? 'bg-primary-50 text-primary-500 font-medium' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-6555">
              All
            </button>
            <button onClick={() => filterApplications('pending')} className={`px-3 py-2 text-sm rounded-md ${applicationFilter === 'pending' ? 'bg-primary-50 text-primary-500 font-medium' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-6556">
              Pending
            </button>
            <button onClick={() => filterApplications('review')} className={`px-3 py-2 text-sm rounded-md ${applicationFilter === 'review' ? 'bg-primary-50 text-primary-500 font-medium' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-6557">
              Under Review
            </button>
            <button onClick={() => filterApplications('accepted')} className={`px-3 py-2 text-sm rounded-md ${applicationFilter === 'accepted' ? 'bg-primary-50 text-primary-500 font-medium' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-6558">
              Accepted
            </button>
          </div>
          {/* Applications list */}
          <div className="p-6" data-id="element-6559">
            {filteredApplications.length > 0 ? <div className="space-y-4" data-id="element-6560">
                {filteredApplications.map(app => <div key={app.id} className="flex flex-col md:flex-row md:items-center justify-between pb-4 border-b border-gray-100 hover:bg-gray-50 p-2 rounded-md transition-colors cursor-pointer" onClick={() => openDetailDrawer(app)} data-id="element-6561">
                    <div className="flex items-center" data-id="element-6562">
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 mr-3" data-id="element-6563">
                        {app.avatar}
                      </div>
                      <div data-id="element-6564">
                        <div className="font-medium text-gray-800 flex items-center gap-2" data-id="element-6565">
                          {app.name}
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(app.status)}`} data-id="element-6566">
                            {app.status === 'pending' ? 'Pending' : app.status === 'accepted' ? 'Accepted' : 'Under Review'}
                          </span>
                          {app.flags && app.flags.length > 0 && <FlagIcon className="w-4 h-4 text-red-500" data-id="element-6567" />}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-6568">
                          {app.program}
                        </div>
                        <div className="text-xs text-gray-400 mt-1" data-id="element-6569">
                          Applied: {app.date}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 md:mt-0 flex flex-col md:items-end" data-id="element-6570">
                      <div className="text-xs text-gray-500 max-w-xs md:text-right line-clamp-2" data-id="element-6571">
                        <span className="font-medium" data-id="element-6572">AI Insight:</span>{' '}
                        {app.aiSummary}
                      </div>
                      {app.tags && app.tags.length > 0 && <div className="flex flex-wrap gap-1 mt-1 md:justify-end" data-id="element-6573">
                          {app.tags.slice(0, 2).map((tag, idx) => <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800" data-id="element-6574">
                              {tag}
                            </span>)}
                        </div>}
                    </div>
                  </div>)}
              </div> : <div className="text-center py-8" data-id="element-6575">
                <div className="mx-auto w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3" data-id="element-6576">
                  <SearchIcon className="w-6 h-6 text-gray-400" data-id="element-6577" />
                </div>
                <h3 className="text-gray-500 font-medium mb-1" data-id="element-6578">
                  No applications found
                </h3>
                <p className="text-gray-400 text-sm" data-id="element-6579">
                  Try adjusting your search or filter criteria
                </p>
              </div>}
          </div>
        </div>
        {/* AI Insights */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-6580">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50" data-id="element-6581">
            <h2 className="text-lg font-medium text-gray-800" data-id="element-6582">AI Insights</h2>
          </div>
          <div className="p-6" data-id="element-6583">
            <div className="space-y-6" data-id="element-6584">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100" data-id="element-6585">
                <div className="flex items-start" data-id="element-6586">
                  <AlertCircleIcon className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-6587" />
                  <div data-id="element-6588">
                    <h3 className="font-medium text-blue-800 mb-1" data-id="element-6589">
                      Suggested Improvements
                    </h3>
                    <p className="text-sm text-blue-700 mb-3" data-id="element-6590">
                      Your MBA program has a 15% lower completion rate compared
                      to similar programs.
                    </p>
                    <div className="flex space-x-2" data-id="element-6591">
                      <button onClick={() => navigate('/university/programs/PRG001')} className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 transition-colors flex items-center" data-id="element-6592">
                        Improve Program
                        <ArrowRightIcon className="w-3 h-3 ml-1" data-id="element-6593" />
                      </button>
                      <button onClick={() => navigate('/university/reports')} className="px-3 py-1.5 bg-white text-blue-600 border border-blue-200 text-xs rounded-md hover:bg-blue-50 transition-colors flex items-center" data-id="element-6594">
                        View Report
                        <ExternalLinkIcon className="w-3 h-3 ml-1" data-id="element-6595" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-4 bg-green-50 rounded-lg border border-green-100" data-id="element-6596">
                <div className="flex items-start" data-id="element-6597">
                  <TrendingUpIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-6598" />
                  <div data-id="element-6599">
                    <h3 className="font-medium text-green-800 mb-1" data-id="element-6600">
                      Application Trend
                    </h3>
                    <p className="text-sm text-green-700 mb-3" data-id="element-6601">
                      Computer Science applications have increased by 28% this
                      semester.
                    </p>
                    <div className="flex space-x-2" data-id="element-6602">
                      <button onClick={() => navigate('/university/reports')} className="px-3 py-1.5 bg-green-600 text-white text-xs rounded-md hover:bg-green-700 transition-colors flex items-center" data-id="element-6603">
                        View Trend Analysis
                        <ArrowRightIcon className="w-3 h-3 ml-1" data-id="element-6604" />
                      </button>
                      <button onClick={() => navigate('/university/reports')} className="px-3 py-1.5 bg-white text-green-600 border border-green-200 text-xs rounded-md hover:bg-green-50 transition-colors flex items-center" data-id="element-6605">
                        Download Report
                        <ExternalLinkIcon className="w-3 h-3 ml-1" data-id="element-6606" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-100" data-id="element-6607">
                <div className="flex items-start" data-id="element-6608">
                  <UsersRoundIcon className="w-5 h-5 text-purple-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-6609" />
                  <div data-id="element-6610">
                    <h3 className="font-medium text-purple-800 mb-1" data-id="element-6611">
                      Student Demographics
                    </h3>
                    <p className="text-sm text-purple-700 mb-3" data-id="element-6612">
                      International student applications have diversified with
                      applicants from 12 new countries.
                    </p>
                    <div className="flex space-x-2" data-id="element-6613">
                      <button onClick={() => navigate('/university/reports')} className="px-3 py-1.5 bg-purple-600 text-white text-xs rounded-md hover:bg-purple-700 transition-colors flex items-center" data-id="element-6614">
                        View Demographics
                        <ArrowRightIcon className="w-3 h-3 ml-1" data-id="element-6615" />
                      </button>
                      <button onClick={() => navigate('/university/communications')} className="px-3 py-1.5 bg-white text-purple-600 border border-purple-200 text-xs rounded-md hover:bg-purple-50 transition-colors flex items-center" data-id="element-6616">
                        Marketing Insights
                        <ExternalLinkIcon className="w-3 h-3 ml-1" data-id="element-6617" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Application Detail Drawer */}
      {isDetailDrawerOpen && selectedApplication && <div className="fixed inset-0 overflow-hidden z-50" data-id="element-6618">
          <div className="absolute inset-0 overflow-hidden" data-id="element-6619">
            <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeDetailDrawer} data-id="element-6620"></div>
            <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex" data-id="element-6621">
              <div className="relative w-screen max-w-2xl" data-id="element-6622">
                <div className="h-full flex flex-col bg-white shadow-xl overflow-y-scroll" data-id="element-6623">
                  {/* Header */}
                  <div className="px-6 py-4 bg-primary-500 sm:px-6" data-id="element-6624">
                    <div className="flex items-start justify-between" data-id="element-6625">
                      <h2 className="text-lg font-medium text-white" data-id="element-6626">
                        Application Details
                      </h2>
                      <button onClick={closeDetailDrawer} className="bg-primary-500 rounded-md text-white hover:text-gray-200 focus:outline-none" data-id="element-6627">
                        <XIcon className="h-6 w-6" data-id="element-6628" />
                      </button>
                    </div>
                  </div>
                  {/* Content */}
                  <div className="flex-1 overflow-y-auto p-6" data-id="element-6629">
                    {/* Student Info */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6" data-id="element-6630">
                      <div className="flex items-center mb-4" data-id="element-6631">
                        <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-xl" data-id="element-6632">
                          {selectedApplication.avatar}
                        </div>
                        <div className="ml-4" data-id="element-6633">
                          <h3 className="text-xl font-medium text-gray-900" data-id="element-6634">
                            {selectedApplication.name}
                          </h3>
                          <p className="text-gray-500" data-id="element-6635">
                            {selectedApplication.studentId}
                          </p>
                          <div className="mt-1 flex items-center" data-id="element-6636">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(selectedApplication.status)}`} data-id="element-6637">
                              {selectedApplication.status === 'pending' ? 'Pending' : selectedApplication.status === 'accepted' ? 'Accepted' : 'Under Review'}
                            </span>
                            <div className="ml-3 flex items-center text-sm text-gray-700" data-id="element-6638">
                              {getEligibilityIcon(selectedApplication.eligibility)}
                              <span className="ml-1" data-id="element-6639">
                                {selectedApplication.eligibility}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Application Summary */}
                      <div className="mb-6" data-id="element-6640">
                        <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-6641">
                          Application Summary
                        </h4>
                        <div className="bg-gray-50 p-4 rounded-md" data-id="element-6642">
                          <div className="grid grid-cols-2 gap-4" data-id="element-6643">
                            <div data-id="element-6644">
                              <div className="text-xs text-gray-500" data-id="element-6645">
                                Program
                              </div>
                              <div className="text-sm font-medium" data-id="element-6646">
                                {selectedApplication.program}
                              </div>
                            </div>
                            <div data-id="element-6647">
                              <div className="text-xs text-gray-500" data-id="element-6648">
                                Intake Period
                              </div>
                              <div className="text-sm font-medium" data-id="element-6649">
                                Fall 2023
                              </div>
                            </div>
                            <div data-id="element-6650">
                              <div className="text-xs text-gray-500" data-id="element-6651">
                                Submitted On
                              </div>
                              <div className="text-sm font-medium" data-id="element-6652">
                                {formatDate(selectedApplication.submissionDate)}
                              </div>
                            </div>
                            <div data-id="element-6653">
                              <div className="text-xs text-gray-500" data-id="element-6654">
                                Last Updated
                              </div>
                              <div className="text-sm font-medium" data-id="element-6655">
                                {formatDate(selectedApplication.lastUpdated)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* AI Insights */}
                      <div className="mb-6" data-id="element-6656">
                        <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-6657">
                          AI Insights
                        </h4>
                        <div className="bg-primary-50 p-4 rounded-md border border-primary-100" data-id="element-6658">
                          <p className="text-sm text-gray-700 mb-3" data-id="element-6659">
                            {selectedApplication.aiSummary}
                          </p>
                          {selectedApplication.flags && selectedApplication.flags.length > 0 && <div className="mb-3" data-id="element-6660">
                                <div className="text-xs font-medium text-red-600 mb-1" data-id="element-6661">
                                  Flags:
                                </div>
                                <ul className="list-disc list-inside text-sm text-red-600" data-id="element-6662">
                                  {selectedApplication.flags.map((flag, index) => <li key={index} data-id="element-6663">{flag}</li>)}
                                </ul>
                              </div>}
                          {selectedApplication.tags && selectedApplication.tags.length > 0 && <div className="flex flex-wrap gap-1" data-id="element-6664">
                                {selectedApplication.tags.map((tag, index) => <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800" data-id="element-6665">
                                    {tag}
                                  </span>)}
                              </div>}
                        </div>
                      </div>
                      {/* Documents */}
                      <div className="mb-6" data-id="element-6666">
                        <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-6667">
                          Uploaded Documents
                        </h4>
                        <div className="space-y-2" data-id="element-6668">
                          {selectedApplication.documents.map((doc, index) => <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-md" data-id="element-6669">
                              <div className="flex items-center" data-id="element-6670">
                                <FileTextIcon className="w-5 h-5 text-gray-400 mr-3" data-id="element-6671" />
                                <span className="text-sm text-gray-700" data-id="element-6672">
                                  {doc}
                                </span>
                              </div>
                              <button className="text-primary-500 hover:text-primary-700 text-sm" data-id="element-6673">
                                View
                              </button>
                            </div>)}
                        </div>
                      </div>
                      {/* Activity Log */}
                      <div data-id="element-6674">
                        <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-6675">
                          Activity Log
                        </h4>
                        <div className="space-y-3" data-id="element-6676">
                          <div className="flex items-start" data-id="element-6677">
                            <div className="flex-shrink-0" data-id="element-6678">
                              <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center" data-id="element-6679">
                                <ClockIcon className="h-4 w-4 text-blue-600" data-id="element-6680" />
                              </div>
                            </div>
                            <div className="ml-3" data-id="element-6681">
                              <p className="text-sm text-gray-700" data-id="element-6682">
                                Application status changed to{' '}
                                <span className="font-medium" data-id="element-6683">
                                  {selectedApplication.status === 'pending' ? 'Pending' : selectedApplication.status === 'accepted' ? 'Accepted' : 'Under Review'}
                                </span>
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-6684">
                                {formatDate(selectedApplication.lastUpdated)} by
                                System
                              </p>
                            </div>
                          </div>
                          <div className="flex items-start" data-id="element-6685">
                            <div className="flex-shrink-0" data-id="element-6686">
                              <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center" data-id="element-6687">
                                <FileTextIcon className="h-4 w-4 text-green-600" data-id="element-6688" />
                              </div>
                            </div>
                            <div className="ml-3" data-id="element-6689">
                              <p className="text-sm text-gray-700" data-id="element-6690">
                                Application submitted
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-6691">
                                {formatDate(selectedApplication.submissionDate)}{' '}
                                by {selectedApplication.name}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Footer Actions */}
                  <div className="flex-shrink-0 px-4 py-4 flex justify-between border-t border-gray-200" data-id="element-6692">
                    <div data-id="element-6693">
                      {selectedApplication.status === 'pending' && <button className="px-4 py-2 bg-red-500 text-white rounded-md text-sm font-medium hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" onClick={() => handleShowRejectModal(selectedApplication.id)} data-id="element-6694">
                          Reject
                        </button>}
                    </div>
                    <div className="flex space-x-3" data-id="element-6695">
                      <button className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500" data-id="element-6696">
                        Message Student
                      </button>
                      {selectedApplication.status === 'pending' && <button className="px-4 py-2 bg-green-500 text-white rounded-md text-sm font-medium hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" onClick={() => handleShowAcceptModal(selectedApplication.id)} data-id="element-6697">
                          Accept
                        </button>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}
      {/* Accept Confirmation Modal */}
      <ConfirmationModal isOpen={isAcceptModalOpen} onClose={() => setIsAcceptModalOpen(false)} onConfirm={handleAcceptConfirm} title="Accept Application" message={`Are you sure you want to accept ${getApplicationInfo(actionApplicationId)}? This will notify the student and move the application to the accepted status.`} confirmButtonText="Accept" confirmButtonColor="bg-green-600" icon={<CheckCircleIcon className="h-6 w-6 text-green-600" aria-hidden="true" data-id="element-6699" />} data-id="element-6698" />
      {/* Reject Confirmation Modal */}
      <ConfirmationModal isOpen={isRejectModalOpen} onClose={() => setIsRejectModalOpen(false)} onConfirm={handleRejectConfirm} title="Reject Application" message={`Are you sure you want to reject ${getApplicationInfo(actionApplicationId)}? This action cannot be undone and will notify the student.`} confirmButtonText="Reject" confirmButtonColor="bg-red-600" icon={<AlertCircleIcon className="h-6 w-6 text-red-600" aria-hidden="true" data-id="element-6701" />} data-id="element-6700" />
    </Layout>;
};
export default UniversityDashboard;